import { useState } from 'react';
import {
  Plus,
  Upload,
  Download,
  Pencil,
  Trash2,
  X,
  FileSpreadsheet,
} from 'lucide-react';
import { Button } from './button';
import { ConfirmModal } from './confirm-modal';
import { toast } from 'sonner';

interface CrudToolbarProps {
  readonly entityName: string;
  readonly entityNamePlural: string;
  readonly selectedCount?: number;
  readonly onAdd?: () => void;
  readonly onEdit?: () => void;
  readonly onDelete?: () => void;
  readonly onBulkUpload?: (file: File) => void;
  readonly onExport?: () => void;
  readonly canAdd?: boolean;
  readonly canEdit?: boolean;
  readonly canDelete?: boolean;
  readonly canBulkUpload?: boolean;
  readonly canExport?: boolean;
  readonly exportData?: Record<string, unknown>[];
  readonly exportFilename?: string;
}

export function CrudToolbar({
  entityName,
  entityNamePlural,
  selectedCount = 0,
  onAdd,
  onEdit,
  onDelete,
  onBulkUpload,
  onExport,
  canAdd = true,
  canEdit = true,
  canDelete = true,
  canBulkUpload = true,
  canExport = true,
  exportData,
  exportFilename,
}: CrudToolbarProps) {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ];
      if (!validTypes.includes(file.type) && !file.name.endsWith('.csv')) {
        toast.error('Please upload a CSV or Excel file');
        return;
      }
      setUploadFile(file);
    }
  };

  const handleUploadConfirm = () => {
    if (uploadFile && onBulkUpload) {
      onBulkUpload(uploadFile);
      toast.success(`Uploading ${uploadFile.name}...`);
    }
    setShowUploadModal(false);
    setUploadFile(null);
  };

  const handleExport = () => {
    if (onExport) {
      onExport();
      return;
    }

    if (exportData && exportData.length > 0) {
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map((row) =>
          headers.map((h) => {
            const val = row[h];
            if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
              return `"${val.replaceAll('"', '""')}"`;
            }
            return val ?? '';
          }).join(',')
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportFilename ?? `${entityNamePlural.toLowerCase()}-export.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${exportData.length} ${entityNamePlural.toLowerCase()}`);
    } else {
      toast.info('No data to export');
    }
  };

  const handleDeleteConfirm = () => {
    if (onDelete) {
      onDelete();
      toast.success(`Deleted ${selectedCount} ${selectedCount === 1 ? entityName.toLowerCase() : entityNamePlural.toLowerCase()}`);
    }
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {canAdd && onAdd && (
          <Button size="sm" onClick={onAdd}>
            <Plus className="mr-2 h-4 w-4" />
            Add {entityName}
          </Button>
        )}

        {canEdit && selectedCount === 1 && onEdit && (
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </Button>
        )}

        {canDelete && selectedCount > 0 && onDelete && (
          <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete ({selectedCount})
          </Button>
        )}

        <div className="flex-1" />

        {canBulkUpload && (
          <Button size="sm" variant="outline" onClick={() => setShowUploadModal(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Bulk Upload
          </Button>
        )}

        {canExport && (
          <Button size="sm" variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        )}
      </div>

      {/* Bulk Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Bulk Upload {entityNamePlural}</h3>
              <button type="button" onClick={() => { setShowUploadModal(false); setUploadFile(null); }} className="rounded-full p-1 hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="rounded-lg border-2 border-dashed border-muted-foreground/25 p-8 text-center">
                <FileSpreadsheet className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground mb-2">
                  Upload a CSV or Excel file
                </p>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="bulk-upload-input"
                />
                <label htmlFor="bulk-upload-input">
                  <Button variant="outline" size="sm" asChild>
                    <span>Choose File</span>
                  </Button>
                </label>
                {uploadFile && (
                  <p className="mt-2 text-sm font-medium text-green-600">
                    Selected: {uploadFile.name}
                  </p>
                )}
              </div>

              <div className="rounded-lg bg-muted p-3">
                <p className="text-xs text-muted-foreground">
                  <strong>Template columns:</strong> Download the export first to see the expected format.
                  Required fields will be validated during import.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => { setShowUploadModal(false); setUploadFile(null); }}>
                Cancel
              </Button>
              <Button onClick={handleUploadConfirm} disabled={!uploadFile}>
                <Upload className="mr-2 h-4 w-4" />
                Upload
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete ${selectedCount} ${selectedCount === 1 ? entityName.toLowerCase() : entityNamePlural.toLowerCase()}? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </>
  );
}
